import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import {
  Ban,
  CalendarDays,
  CheckCircle2,
  ExternalLink,
  FileDown,
  Lock,
  MonitorPlay,
  Save,
  Search,
  Users,
  X,
} from "lucide-react";

import { ApiError, formatCLP, formatDate } from "../../lib/api";
import { ASAMBLEAS_APP_URL } from "../../lib/asambleasApp";
import { useDebounced } from "../../lib/debounce";
import { matchSocio } from "../../lib/sociosSearch";
import { useToast } from "../../components/Toast";
import {
  Button,
  Card,
  Cargando,
  ConfirmDialog,
  ErrorCarga,
  PageHeader,
  TableWrap,
} from "../../components/ui";
import {
  cancelarReunion,
  cargarAsistencias,
  cerrarReunion,
  exportarAsistenciaCsv,
  listarAsistencias,
  listarTipos,
  obtenerReunion,
  type AsistenciaItem,
  type AsistenciaRead,
  type EstadoAsistencia,
  type ReunionRead,
} from "./api";
import { listarSocios, type SocioRead } from "../socios/api";
import { EstadoReunionBadge } from "./estado";

const ESTADOS: EstadoAsistencia[] = ["Presente", "Ausente", "Justificado"];

export function AsambleaDetailPage(): JSX.Element {
  const params = useParams<{ reunionId: string }>();
  const reunionId = Number(params.reunionId);

  const reunionQ = useQuery({
    queryKey: ["reunion", reunionId],
    queryFn: () => obtenerReunion(reunionId),
    enabled: !Number.isNaN(reunionId),
  });
  const tiposQ = useQuery({ queryKey: ["tipos-reunion"], queryFn: listarTipos });
  const asistenciasQ = useQuery({
    queryKey: ["reunion-asistencias", reunionId],
    queryFn: () => listarAsistencias(reunionId),
    enabled: !Number.isNaN(reunionId),
  });
  const sociosQ = useQuery({
    queryKey: ["socios", { only_active: true, only_socios: true }],
    queryFn: () => listarSocios({ only_active: true, only_socios: true }),
  });

  if (Number.isNaN(reunionId)) {
    return <ErrorCarga mensaje="ID de reunión inválido" />;
  }
  if (reunionQ.isPending) {
    return <Cargando />;
  }
  if (reunionQ.isError || !reunionQ.data) {
    return (
      <div className="space-y-4">
        <PageHeader
          title="Asamblea"
          volver={{ etiqueta: "Asambleas", ruta: "/asambleas" }}
        />
        <ErrorCarga
          error={reunionQ.error}
          mensaje="Error al cargar reunión."
          onReintentar={() => void reunionQ.refetch()}
        />
      </div>
    );
  }

  const reunion = reunionQ.data;
  const tipo = tiposQ.data?.find((t) => t.id === reunion.tipo_reunion_id);

  return (
    <div className="space-y-4">
      <CabeceraReunion reunion={reunion} tipoNombre={tipo?.nombre ?? null} />

      <AsistenciaSection
        reunion={reunion}
        asistencias={asistenciasQ.data ?? []}
        socios={sociosQ.data ?? []}
        loading={asistenciasQ.isPending || sociosQ.isPending}
      />
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Cabecera
// ────────────────────────────────────────────────────────────────────────

function CabeceraReunion({
  reunion,
  tipoNombre,
}: {
  reunion: ReunionRead;
  tipoNombre: string | null;
}): JSX.Element {
  const qc = useQueryClient();
  const toast = useToast();
  const [error, setError] = useState<string | null>(null);
  const [cancelarConfirm, setCancelarConfirm] = useState(false);

  const cancelar = useMutation({
    mutationFn: () => cancelarReunion(reunion.id),
    onSuccess: () => {
      setCancelarConfirm(false);
      toast.success("Reunión cancelada");
      void qc.invalidateQueries({ queryKey: ["reunion", reunion.id] });
      void qc.invalidateQueries({ queryKey: ["reuniones"] });
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : "No se pudo cancelar";
      setCancelarConfirm(false);
      setError(msg);
      toast.error(msg);
    },
  });

  const editable =
    reunion.estado !== "Cerrada" && reunion.estado !== "Cancelada";

  return (
    <>
      <PageHeader
        title={`Asamblea del ${formatDate(reunion.fecha)}`}
        icon={CalendarDays}
        subtitle={
          <span className="inline-flex flex-wrap items-center gap-2">
            {tipoNombre ?? `Tipo #${reunion.tipo_reunion_id}`} ·{" "}
            {reunion.hora.slice(0, 5)}
            <EstadoReunionBadge estado={reunion.estado} />
          </span>
        }
        volver={{ etiqueta: "Asambleas", ruta: "/asambleas" }}
        actions={
          <>
            {/* Proyección, marcado desde el celular y votaciones viven en la app
                de asambleas (Asambleas v2): mismo backend, misma reunión. */}
            {reunion.estado !== "Cancelada" && (
              <>
                <a
                  href={`${ASAMBLEAS_APP_URL}/pantalla/${reunion.id}`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 rounded-md bg-white px-2.5 py-1 text-xs font-medium text-neutral-700 shadow-sm ring-1 ring-neutral-300 hover:bg-neutral-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
                  title="Pantalla de sala para el proyector (quórum y votación en vivo)"
                >
                  <MonitorPlay className="h-3.5 w-3.5" aria-hidden="true" /> Proyectar
                </a>
                <a
                  href={`${ASAMBLEAS_APP_URL}/reuniones/${reunion.id}`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 rounded-md bg-white px-2.5 py-1 text-xs font-medium text-brand-700 shadow-sm ring-1 ring-brand-300 hover:bg-brand-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
                  title="Asamblea en vivo: marcado desde el celular, poderes con firma, votaciones"
                >
                  <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" /> Abrir en Asambleas
                </a>
              </>
            )}
            {editable && (
              <Button
                variant="linkDanger"
                size="sm"
                icon={Ban}
                onClick={() => setCancelarConfirm(true)}
                disabled={cancelar.isPending}
              >
                {cancelar.isPending ? "…" : "Cancelar reunión"}
              </Button>
            )}
          </>
        }
      />

      <Card className="space-y-3">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
        <Stat label="Lugar" value={reunion.lugar ?? "—"} />
        <Stat
          label="Genera multa"
          value={reunion.genera_multa ? "Sí" : "No"}
          tone={reunion.genera_multa ? "warn" : "muted"}
        />
        <Stat label="Estado" value={reunion.estado} />
        <Stat label="ID" value={`#${reunion.id}`} />
        {reunion.observaciones && (
          <div className="col-span-full text-xs text-neutral-600 italic">
            {reunion.observaciones}
          </div>
        )}
      </div>

      {error && <p className="text-sm text-rose-700">{error}</p>}
      </Card>

      {cancelarConfirm && (
        <ConfirmDialog
          open
          title="Cancelar la reunión"
          description={`Se cancela ${tipoNombre ?? "la reunión"} del ${formatDate(reunion.fecha)} a las ${reunion.hora.slice(0, 5)}. No se generan multas y no se puede deshacer.`}
          confirmLabel="Cancelar reunión"
          cancelLabel="Volver"
          loading={cancelar.isPending}
          onCancel={() => setCancelarConfirm(false)}
          onConfirm={() => cancelar.mutate()}
        />
      )}
    </>
  );
}

function Stat({
  label,
  value,
  tone = "muted",
}: {
  label: string;
  value: string;
  tone?: "muted" | "warn";
}): JSX.Element {
  const cls =
    tone === "warn"
      ? "border-amber-100 bg-amber-50 text-amber-900"
      : "border-neutral-100 bg-neutral-50 text-neutral-900";
  return (
    <div className={`rounded-md border p-2 ${cls}`}>
      <div className="text-xs uppercase tracking-wider text-neutral-500">
        {label}
      </div>
      <div className="mt-1 text-sm font-semibold">{value}</div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Asistencia
// ────────────────────────────────────────────────────────────────────────

interface SocioRow {
  socio_id: number;
  nombre: string;
  rut: string;
  numero_socio: number | null;
  numero_libro: string | null;
  telefono: string | null;
  correo: string | null;
}

function AsistenciaSection({
  reunion,
  asistencias,
  socios: sociosData,
  loading,
}: {
  reunion: ReunionRead;
  asistencias: AsistenciaRead[];
  socios: SocioRead[];
  loading: boolean;
}): JSX.Element {
  const qc = useQueryClient();
  const toast = useToast();

  const socios: SocioRow[] = useMemo(
    () =>
      sociosData
        .map((s) => ({
          socio_id: s.id,
          nombre: s.nombre,
          rut: s.rut,
          numero_socio: s.numero_socio,
          numero_libro: s.numero_libro,
          telefono: s.telefono,
          correo: s.correo,
        }))
        .sort((a, b) => a.nombre.localeCompare(b.nombre, "es")),
    [sociosData],
  );

  // Buscador estilo /socios: nombre, apellido, RUT (con o sin formato),
  // N° socio, libro, teléfono, correo. Multi-token con AND.
  const [filtro, setFiltro] = useState("");
  const filtroDeb = useDebounced(filtro, 200);
  const sociosVisibles = useMemo(
    () => socios.filter((s) => matchSocio(s, filtroDeb)),
    [socios, filtroDeb],
  );

  const asistenciaPorSocio = useMemo(() => {
    const m = new Map<number, AsistenciaRead>();
    for (const a of asistencias) m.set(a.socio_id, a);
    return m;
  }, [asistencias]);

  const [draft, setDraft] = useState<Map<number, EstadoAsistencia>>(
    () => new Map(),
  );
  const [obs, setObs] = useState<Map<number, string>>(() => new Map());
  const [error, setError] = useState<string | null>(null);
  const [cerrarConfirm, setCerrarConfirm] = useState(false);

  // Sincronizar draft cuando llegan/cambian asistencias del servidor
  useEffect(() => {
    const next = new Map<number, EstadoAsistencia>();
    const obsNext = new Map<number, string>();
    for (const a of asistencias) {
      next.set(a.socio_id, a.estado);
      if (a.observacion) obsNext.set(a.socio_id, a.observacion);
    }
    setDraft(next);
    setObs(obsNext);
  }, [asistencias]);

  const guardar = useMutation({
    mutationFn: (items: AsistenciaItem[]) =>
      cargarAsistencias(reunion.id, items),
    onSuccess: (items) => {
      toast.success(`${items.length} asistencia(s) guardadas`);
      void qc.invalidateQueries({
        queryKey: ["reunion-asistencias", reunion.id],
      });
      void qc.invalidateQueries({ queryKey: ["reunion", reunion.id] });
      void qc.invalidateQueries({ queryKey: ["reuniones"] });
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : "No se pudo guardar";
      setError(msg);
      toast.error(msg);
    },
  });

  const cerrar = useMutation({
    mutationFn: () => cerrarReunion(reunion.id),
    onSuccess: (res) => {
      setCerrarConfirm(false);
      if (res.multas_generadas === 0) {
        toast.success("Reunión cerrada sin multas generadas");
      } else {
        toast.success(
          `Reunión cerrada · ${res.multas_generadas} multa(s) de ${formatCLP(
            res.monto_unitario,
          )} c/u generadas`,
        );
      }
      void qc.invalidateQueries({ queryKey: ["reunion", reunion.id] });
      void qc.invalidateQueries({ queryKey: ["reuniones"] });
      void qc.invalidateQueries({ queryKey: ["multas-pendientes"] });
    },
    onError: (err) => {
      setCerrarConfirm(false);
      const msg = err instanceof ApiError ? err.message : "No se pudo cerrar";
      setError(msg);
      toast.error(msg);
    },
  });

  const cerrada = reunion.estado === "Cerrada";
  const cancelada = reunion.estado === "Cancelada";
  const editable = !cerrada && !cancelada;

  const counts = useMemo(() => {
    const c = { Presente: 0, Ausente: 0, Justificado: 0, total: socios.length };
    for (const s of socios) {
      const e = draft.get(s.socio_id);
      if (e) c[e] += 1;
    }
    return c;
  }, [draft, socios]);

  function setEstado(socioId: number, estado: EstadoAsistencia): void {
    setDraft((d) => {
      const m = new Map(d);
      m.set(socioId, estado);
      return m;
    });
  }
  function setObsFor(socioId: number, v: string): void {
    setObs((o) => {
      const m = new Map(o);
      if (v.trim()) m.set(socioId, v);
      else m.delete(socioId);
      return m;
    });
  }
  function marcarTodos(estado: EstadoAsistencia): void {
    // Si hay filtro activo, marcamos solo los visibles (subgrupo). Útil en
    // asambleas grandes donde el operador quiere marcar Presente a quienes
    // están en una mesa específica filtrando por número.
    const objetivo = filtroDeb.trim() ? sociosVisibles : socios;
    setDraft((d) => {
      const m = new Map(d);
      for (const s of objetivo) m.set(s.socio_id, estado);
      return m;
    });
  }
  function payload(): AsistenciaItem[] {
    const items: AsistenciaItem[] = [];
    for (const s of socios) {
      const estado = draft.get(s.socio_id);
      if (!estado) continue;
      items.push({
        socio_id: s.socio_id,
        estado,
        observacion: obs.get(s.socio_id) ?? null,
      });
    }
    return items;
  }

  return (
    <section className="rounded-lg bg-white ring-1 ring-neutral-200 p-4 space-y-3">
      <header className="flex flex-wrap items-center gap-2">
        <Users className="h-5 w-5 text-neutral-500" strokeWidth={1.5} />
        <h2 className="text-base font-semibold text-neutral-900">Asistencia</h2>
        <span className="text-xs text-neutral-500">
          {counts.Presente} presente · {counts.Ausente} ausente ·{" "}
          {counts.Justificado} justificado · {counts.total} socio(s)
          {filtroDeb.trim() && (
            <span className="ml-2 text-brand-700">
              · {sociosVisibles.length} visibles
            </span>
          )}
        </span>
        <div className="ml-auto flex gap-2 flex-wrap">
          <Button
            variant="secondary"
            size="sm"
            icon={FileDown}
            onClick={() => void exportarAsistenciaCsv(reunion.id).catch(() => toast.error("No se pudo exportar"))}
            title="Lista de asistencia en CSV para el libro de actas"
          >
            Exportar asistencia
          </Button>
          {editable && (
            <>
              <button
                type="button"
                onClick={() => marcarTodos("Presente")}
                className="rounded-md bg-emerald-50 px-2 py-1 text-xs text-emerald-700 hover:bg-emerald-100"
                disabled={guardar.isPending}
                title={
                  filtroDeb.trim()
                    ? "Marca Presente solo a los socios visibles (filtrados)"
                    : "Marca Presente a todos los socios"
                }
              >
                Marcar {filtroDeb.trim() ? "visibles" : "todos"} Presente
              </button>
              <button
                type="button"
                onClick={() => marcarTodos("Ausente")}
                className="rounded-md bg-rose-50 px-2 py-1 text-xs text-rose-700 hover:bg-rose-100"
                disabled={guardar.isPending}
                title={
                  filtroDeb.trim()
                    ? "Marca Ausente solo a los socios visibles (filtrados)"
                    : "Marca Ausente a todos los socios"
                }
              >
                Marcar {filtroDeb.trim() ? "visibles" : "todos"} Ausente
              </button>
            </>
          )}
        </div>
      </header>

      {socios.length > 0 && (
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-500" strokeWidth={1.5} />
          <input
            type="search"
            aria-label="Buscar socio"
            placeholder="Buscar por nombre, apellido, RUT, N° socio, libro, teléfono o correo…"
            value={filtro}
            onChange={(e) => setFiltro(e.target.value)}
            className="w-full pl-9 pr-9 py-2 rounded-md border border-neutral-450 text-sm shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
          />
          {filtro && (
            <button
              type="button"
              onClick={() => setFiltro("")}
              aria-label="Limpiar búsqueda"
              className="absolute right-2 top-1/2 -translate-y-1/2 text-neutral-500 hover:text-neutral-700"
              title="Limpiar búsqueda"
            >
              <X className="h-4 w-4" strokeWidth={1.5} />
            </button>
          )}
        </div>
      )}

      {cerrada && (
        <div className="rounded-md border border-emerald-100 bg-emerald-50 p-3 text-sm text-emerald-900 flex items-center gap-2">
          <Lock className="h-4 w-4" strokeWidth={1.5} />
          La reunión está Cerrada. Las multas a inasistentes ya se generaron.
          Si hubo un error de marcado, anula la multa desde Multas.
        </div>
      )}
      {cancelada && (
        <div className="rounded-md border border-neutral-200 bg-neutral-50 p-3 text-sm text-neutral-700 flex items-center gap-2">
          <Ban className="h-4 w-4" strokeWidth={1.5} />
          La reunión está Cancelada.
        </div>
      )}

      {loading ? (
        <Cargando inline />
      ) : socios.length === 0 ? (
        <p className="text-sm text-amber-700">
          No hay socios marcados como "Es Socio" (con derecho a asamblea).
          Ve al módulo Socios y promueve al menos uno.
        </p>
      ) : sociosVisibles.length === 0 ? (
        <p className="text-sm text-neutral-500">
          Sin coincidencias para "{filtroDeb.trim()}".
        </p>
      ) : (
        <TableWrap label="Asistencia de socios">
          <table className="min-w-full divide-y divide-neutral-200 text-sm">
            <thead className="bg-neutral-50 text-xs uppercase tracking-wider text-neutral-500">
              <tr>
                <th scope="col" className="px-3 py-2 text-left">Socio</th>
                <th scope="col" className="px-3 py-2 text-left">Estado</th>
                <th scope="col" className="px-3 py-2 text-left">Observación</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {sociosVisibles.map((s) => {
                const persistido = asistenciaPorSocio.get(s.socio_id);
                const estado = draft.get(s.socio_id);
                return (
                  <tr key={s.socio_id} className="hover:bg-neutral-50">
                    <td className="px-3 py-2">
                      <div className="font-medium text-neutral-900">
                        {s.nombre}
                      </div>
                      <div className="text-xs text-neutral-500">{s.rut}</div>
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex flex-wrap gap-1">
                        {ESTADOS.map((e) => (
                          <button
                            key={e}
                            type="button"
                            disabled={!editable || guardar.isPending}
                            onClick={() => setEstado(s.socio_id, e)}
                            aria-pressed={estado === e}
                            className={`rounded-full px-2 py-0.5 text-xs border ${
                              estado === e
                                ? toneActive(e)
                                : "border-neutral-200 text-neutral-500 hover:bg-neutral-100"
                            } disabled:opacity-60`}
                          >
                            {e}
                          </button>
                        ))}
                        {!estado && (
                          <span className="text-xs text-neutral-500 italic ml-1">
                            sin marcar
                          </span>
                        )}
                      </div>
                      {persistido && (
                        <div className="mt-0.5 text-xs uppercase text-neutral-500">
                          guardado: {persistido.estado}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <input
                        type="text"
                        maxLength={255}
                        value={obs.get(s.socio_id) ?? ""}
                        onChange={(e) =>
                          setObsFor(s.socio_id, e.target.value)
                        }
                        disabled={!editable || guardar.isPending}
                        placeholder="ej. Certificado médico"
                        className="w-full rounded-md border border-neutral-200 px-2 py-1 text-xs disabled:bg-neutral-50"
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </TableWrap>
      )}

      {error && <p className="text-sm text-rose-700">{error}</p>}

      {editable && socios.length > 0 && (
        <div className="flex flex-wrap gap-2 justify-end pt-2 border-t border-neutral-100">
          <Button
            icon={Save}
            onClick={() => {
              setError(null);
              const p = payload();
              if (p.length === 0)
                return setError("Marca al menos una asistencia");
              guardar.mutate(p);
            }}
            disabled={guardar.isPending}
          >
            {guardar.isPending ? "Guardando…" : "Guardar asistencia"}
          </Button>
          {/* Cerrar es "éxito/forward" (emerald), no destructivo — se mantiene
              el color semántico; el confirm va por ConfirmDialog. */}
          <button
            type="button"
            onClick={() => setCerrarConfirm(true)}
            disabled={cerrar.isPending}
            className="inline-flex items-center gap-2 rounded-md bg-emerald-600 px-3 py-1.5 text-sm font-medium text-white shadow-sm transition-colors hover:bg-emerald-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <CheckCircle2 className="h-4 w-4" strokeWidth={1.5} />
            {cerrar.isPending
              ? "Cerrando…"
              : reunion.genera_multa
                ? "Cerrar y generar multas"
                : "Cerrar reunión"}
          </button>
        </div>
      )}

      {cerrarConfirm && (
        <ConfirmDialog
          open
          tone="default"
          title="Cerrar la reunión"
          description={
            reunion.genera_multa
              ? "Se cierra la reunión y se generan multas por inasistencia a cada socio marcado Ausente. No se puede deshacer."
              : "Se cierra la reunión. No se generan multas. No se puede deshacer."
          }
          confirmLabel={
            reunion.genera_multa ? "Cerrar y generar multas" : "Cerrar reunión"
          }
          loading={cerrar.isPending}
          onCancel={() => setCerrarConfirm(false)}
          onConfirm={() => cerrar.mutate()}
        />
      )}
    </section>
  );
}

function toneActive(estado: EstadoAsistencia): string {
  switch (estado) {
    case "Presente":
      return "border-emerald-300 bg-emerald-100 text-emerald-800";
    case "Ausente":
      return "border-rose-300 bg-rose-100 text-rose-800";
    case "Justificado":
      return "border-amber-300 bg-amber-100 text-amber-800";
  }
}
